import { useMemo, useState } from "react";
import PageHero from "@/components/base/PageHero";
import CharacterCard from "@/components/base/CharacterCard";
import { characters } from "@/mocks/characters";
import { useSeo } from "@/hooks/useSeo";

type FilterKey = "all" | "lead" | "support";

const filters: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部角色" },
  { key: "lead", label: "主要角色" },
  { key: "support", label: "配角" },
];

export default function CharactersPage() {
  useSeo({
    title: "剧中角色介绍 - 年轻的母亲2",
    description:
      "《年轻的母亲2》剧中角色介绍，涵盖主要角色与配角的人物背景、扮演者信息及彼此之间的复杂关系，帮助观众理解人物动机与剧情走向。",
    keywords: "年轻的母亲2角色,人物介绍,角色分析,人物关系,主角配角",
  });

  const [filter, setFilter] = useState<FilterKey>("all");

  const list = useMemo(
    () => (filter === "all" ? characters : characters.filter((c) => c.roleType === filter)),
    [filter],
  );

  const leadCount = characters.filter((c) => c.roleType === "lead").length;
  const supportCount = characters.length - leadCount;

  return (
    <div className="w-full">
      <PageHero
        eyebrow="角色分析"
        title="剧中角色介绍"
        desc="一个家庭的故事，由一群各有来处的人共同完成。这里汇总了《年轻的母亲2》的主要角色与配角，包含扮演者信息、人物背景与关系解读。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "角色分析" }]}
      />

      <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <p className="font-label text-xs font-semibold uppercase tracking-wide text-primary-600">
              主要角色
            </p>
            <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">
              {leadCount} 位
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              推动主线剧情、承载核心情感与成长弧光的角色。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <p className="font-label text-xs font-semibold uppercase tracking-wide text-accent-600">
              配角
            </p>
            <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">
              {supportCount} 位
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              串联支线、映照主角处境，让故事更真实丰满的人物。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 sm:col-span-2 lg:col-span-1">
            <p className="font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
              人物关系
            </p>
            <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">
              一个家
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              婆媳、夫妻、母女、兄妹、闺蜜与同事，构成一张真实的关系网。
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                filter === f.key
                  ? "bg-primary-500 font-semibold text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-sm text-foreground-500">共 {list.length} 位角色</span>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((ch) => (
            <CharacterCard key={ch.id} {...ch} />
          ))}
        </div>
      </div>
    </div>
  );
}